/**
 * App root component
 * Sets up providers and routing
 */

import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ConfigProvider, App as AntApp, theme as antTheme, Spin } from 'antd';
// NOTE: Toaster is provided by ToastProvider in AppLayout - do not add another here

import { AppLayout } from '@/components/layout';
import {
  DashboardPage,
  NewJobPage,
  JobsPage,
  JobDetailsPage,
  ResultsPage,
  MapsPage,
  SettingsPage,
  StatsPage,
  NotFoundPage,
  SharedListPage,
  SiteLoginPage,
  SchedulesPage,
  NewSchedulePage,
  SocialLookupPage,
} from '@/pages';
import { usePreferencesStore } from '@/stores/usePreferencesStore';
import { useSiteAuth } from '@/stores/useSiteAuthStore';
import { theme } from '@/styles/theme';

// Create a query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes (formerly cacheTime)
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});

function AppContent() {
  const { theme: themeMode } = usePreferencesStore();
  const isDark = themeMode === 'dark';
  const { isInitialized, isLoading, requiresLogin, isAdmin, checkStatus } = useSiteAuth();

  // Check if we're on a public shared list page (bypass auth)
  const isSharedListPage = window.location.pathname.startsWith('/shared/');

  // Check site auth status on mount (skip for shared list pages)
  useEffect(() => {
    if (!isSharedListPage) {
      checkStatus();
    }
  }, [checkStatus, isSharedListPage]);

  // Get the Ant Design config for consistent theming
  const antConfig = {
    algorithm: isDark ? antTheme.darkAlgorithm : antTheme.defaultAlgorithm,
    token: {
      colorPrimary: theme.colors.primary,
      colorSuccess: theme.colors.success,
      colorWarning: theme.colors.warning,
      colorError: theme.colors.error,
      colorInfo: theme.colors.info,
      borderRadius: theme.borderRadius.md,
      fontFamily: theme.fonts.body,
    },
    components: {
      Card: {
        paddingLG: 24,
      },
      Table: {
        headerBg: isDark ? '#1f1f1f' : '#fafafa',
      },
      Layout: {
        siderBg: isDark ? '#141414' : '#ffffff',
        headerBg: isDark ? '#141414' : '#ffffff',
        bodyBg: isDark ? '#0a0a0a' : '#f5f5f5',
      },
    },
  };

  // Show loading while checking auth status (skip for shared list pages)
  if (!isSharedListPage && (!isInitialized || isLoading)) {
    return (
      <ConfigProvider theme={antConfig}>
        <AntApp>
          <div
            style={{
              minHeight: '100vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: isDark ? '#0a0a0a' : '#f5f5f5',
            }}
          >
            <Spin size="large" />
          </div>
        </AntApp>
      </ConfigProvider>
    );
  }

  // Show login page if authentication is required (skip for shared list pages)
  if (!isSharedListPage && requiresLogin) {
    return (
      <ConfigProvider theme={antConfig}>
        <AntApp>
          <SiteLoginPage />
        </AntApp>
      </ConfigProvider>
    );
  }

  return (
    <ConfigProvider theme={antConfig}>
      <AntApp>
        <BrowserRouter>
          <Routes>
            {/* Public shared list page (no layout) */}
            <Route path="/shared/:slug" element={<SharedListPage />} />

            {/* Main app routes with layout */}
            <Route path="/" element={<AppLayout />}>
              {isAdmin ? (
                <>
                  <Route index element={<DashboardPage />} />
                  <Route path="jobs" element={<JobsPage />} />
                  <Route path="jobs/new" element={<NewJobPage />} />
                  <Route path="jobs/:jobId" element={<JobDetailsPage />} />
                  <Route path="results" element={<ResultsPage />} />
                  <Route path="social-lookup" element={<SocialLookupPage />} />
                  <Route path="maps" element={<MapsPage />} />
                  <Route path="schedules" element={<SchedulesPage />} />
                  <Route path="schedules/new" element={<NewSchedulePage />} />
                  <Route path="settings" element={<SettingsPage />} />
                  <Route path="stats" element={<StatsPage />} />
                </>
              ) : (
                <>
                  <Route index element={<Navigate to="/results" replace />} />
                  <Route path="results" element={<ResultsPage />} />
                  <Route path="social-lookup" element={<SocialLookupPage />} />
                </>
              )}
              <Route path="404" element={<NotFoundPage />} />
              <Route path="*" element={<Navigate to={isAdmin ? '/404' : '/results'} replace />} />
            </Route>
          </Routes>
        </BrowserRouter>
        {/* Toaster is provided by ToastProvider in AppLayout */}
      </AntApp>
    </ConfigProvider>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AppContent />
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

export default App;
